"use client";

import { useState } from "react";
import { submitSupportMessage } from "@/lib/actions/community";

const TOPICS = ["Question", "Bug report", "Idea or request", "Account help", "Something else"];

export function ContactForm({ defaultEmail }: { defaultEmail?: string | null }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState(defaultEmail ?? "");
  const [topic, setTopic] = useState(TOPICS[0]);
  const [body, setBody] = useState("");
  const [company, setCompany] = useState(""); // honeypot
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">("idle");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setState("busy");
    try {
      const res = await submitSupportMessage({ name, email, topic, body, company });
      setState(res.ok ? "done" : "error");
    } catch {
      setState("error");
    }
  }

  if (state === "done") {
    return (
      <div className="rounded-[14px] border border-border bg-surface p-6 text-center">
        <p className="font-serif text-[20px] font-semibold text-ink">Message sent</p>
        <p className="mx-auto mt-1.5 max-w-[34em] text-[13.5px] leading-relaxed text-ink-3">
          Thanks — we read everything and reply to <strong className="text-ink-2">{email.trim()}</strong> as soon as
          we can.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-[14px] border border-border bg-surface p-5">
      <input
        type="text"
        value={company}
        onChange={(e) => setCompany(e.target.value)}
        name="company"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        className="absolute left-[-9999px] h-px w-px opacity-0"
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-[.12em] text-muted-3">Name (optional)</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 h-[40px] w-full rounded-[9px] border border-border bg-paper px-3 text-[13.5px] text-ink outline-none focus:outline-2 focus:outline-copper"
          />
        </label>
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-[.12em] text-muted-3">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 h-[40px] w-full rounded-[9px] border border-border bg-paper px-3 text-[13.5px] text-ink outline-none focus:outline-2 focus:outline-copper"
          />
        </label>
      </div>
      <label className="mt-3 block">
        <span className="text-[11px] font-bold uppercase tracking-[.12em] text-muted-3">Topic</span>
        <select
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          className="mt-1 h-[40px] w-full rounded-[9px] border border-border bg-paper px-2.5 text-[13.5px] text-ink outline-none focus:outline-2 focus:outline-copper sm:w-[280px]"
        >
          {TOPICS.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>
      </label>
      <label className="mt-3 block">
        <span className="text-[11px] font-bold uppercase tracking-[.12em] text-muted-3">Message</span>
        <textarea
          required
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={6}
          maxLength={4000}
          placeholder="What's going on? If it's a bug, what you did and what you expected helps a lot."
          className="mt-1 w-full resize-y rounded-[9px] border border-border bg-paper px-3 py-2.5 text-[13.5px] leading-relaxed text-ink outline-none focus:outline-2 focus:outline-copper"
        />
      </label>
      {state === "error" && (
        <p className="mt-2 text-[13px] font-semibold text-danger" role="alert">
          That didn&apos;t send — check the email address and try again.
        </p>
      )}
      <button
        type="submit"
        disabled={state === "busy" || !body.trim() || !email.trim()}
        className="mt-4 h-[42px] rounded-[10px] bg-green px-5 text-[13.5px] font-bold text-surface disabled:opacity-40"
      >
        {state === "busy" ? "Sending…" : "Send message"}
      </button>
    </form>
  );
}
